import React, { useState, useEffect, useRef } from "react";
import * as Mui from "@mui/material";
import { makeStyles } from "tss-react/mui";
import * as Icons from "react-feather";

const MAX_PER_SIDE = 10; // includes the 1 locked chip per side (No Min / No Max)

export const DEFAULT_MIN_PRICES = [60000, 180000, 350000, 500000, 600000, 700000, 900000, 1000000, 1200000];
export const DEFAULT_MAX_PRICES = [200000, 300000, 400000, 500000, 600000, 900000, 1200000, 1600000, 1800000];

export function formatPrice(value: number): string {
	if (value >= 1000000) {
		const m = value / 1000000;
		return `$${Number.isInteger(m) ? m : m.toFixed(1)}m`;
	}
	if (value < 1000) {
		return `$${value}`;
	}
	return `$${value / 1000}k`;
}

const useStyles = makeStyles()((theme) => ({
	priceChip: {
		backgroundColor: theme.palette.primary.faded,
		height: 36,
		fontSize: 14,
		paddingLeft: theme.spacing(0.5),
		paddingRight: theme.spacing(0.5),
		"& .MuiChip-deleteIcon": {
			background: "none",
			color: "#000000",
			"&:hover": {
				background: "none",
				color: "#000000",
			},
		},
	},
	lockedChip: {
		backgroundColor: theme.palette.grey[200],
		height: 36,
		fontSize: 14,
		paddingLeft: theme.spacing(0.5),
		paddingRight: theme.spacing(0.5),
		"& .MuiChip-deleteIcon": {
			background: "none",
			cursor: "default",
		},
	},
	limitPopoverPaper: {
		padding: theme.spacing(1.5, 2),
		backgroundColor: "#FFF3F0",
		maxWidth: 260,
		position: "relative",
		overflow: "visible",
		boxShadow: theme.shadows[2],
		"&::before": {
			content: '""',
			position: "absolute",
			top: -8,
			left: 16,
			width: 0,
			height: 0,
			borderLeft: "8px solid transparent",
			borderRight: "8px solid transparent",
			borderBottom: "8px solid #FFF3F0",
		},
	},
}));

interface Props {
	open: boolean;
	minPriceOptions: number[];
	maxPriceOptions: number[];
	onSave: (minPrices: number[], maxPrices: number[]) => void;
	onCancel: () => void;
}

export const PriceRangeOptionsModal: React.FC<Props> = ({ open, minPriceOptions, maxPriceOptions, onSave, onCancel }) => {
	const { classes } = useStyles();
	const [minPrices, setMinPrices] = useState<number[]>([]);
	const [maxPrices, setMaxPrices] = useState<number[]>([]);
	const [minInput, setMinInput] = useState("");
	const [maxInput, setMaxInput] = useState("");
	const [minError, setMinError] = useState("");
	const [maxError, setMaxError] = useState("");
	const [minLimitOpen, setMinLimitOpen] = useState(false);
	const [maxLimitOpen, setMaxLimitOpen] = useState(false);
	const minInputRef = useRef<HTMLDivElement>(null);
	const maxInputRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (open) {
			setMinPrices(minPriceOptions.length > 0 ? [...minPriceOptions] : [...DEFAULT_MIN_PRICES]);
			setMaxPrices(maxPriceOptions.length > 0 ? [...maxPriceOptions] : [...DEFAULT_MAX_PRICES]);
			setMinInput("");
			setMaxInput("");
			setMinError("");
			setMaxError("");
			setMinLimitOpen(false);
			setMaxLimitOpen(false);
		}
	}, [open]);

	const handleAdd = (
		input: string,
		prices: number[],
		setPrices: (v: number[]) => void,
		setInput: (v: string) => void,
		setError: (v: string) => void,
	) => {
		const trimmed = input.trim();
		const value = parseInt(trimmed.replace(/,/g, ""), 10);
		if (prices.includes(value)) {
			setError("This value is already in the list.");
			return;
		}
		setPrices([...prices, value].sort((a, b) => a - b));
		setInput("");
		setError("");
	};

	const renderColumn = (
		label: string,
		lockedLabel: string,
		lockedPosition: "first" | "last",
		prices: number[],
		setPrices: (v: number[]) => void,
		input: string,
		setInput: (v: string) => void,
		error: string,
		setError: (v: string) => void,
		limitOpen: boolean,
		setLimitOpen: (v: boolean) => void,
		inputRef: React.RefObject<HTMLDivElement>,
	) => {
		const atLimit = prices.length >= MAX_PER_SIDE - 1;
		const count = prices.length + 1; // +1 for the locked chip

		const lockedChip = (
			<Mui.Box key="locked" mb={1.5}>
				<Mui.Chip
					label={lockedLabel}
					size="small"
					className={classes.lockedChip}
					deleteIcon={<Icons.Lock size={12} />}
					onDelete={() => {/* locked — no-op */}}
				/>
			</Mui.Box>
		);

		const valueChips = prices.map((price) => (
			<Mui.Box key={price} mb={1.5}>
				<Mui.Chip
					label={formatPrice(price)}
					size="small"
					className={classes.priceChip}
					deleteIcon={<Icons.X size={14} />}
					onDelete={() => setPrices(prices.filter((p) => p !== price))}
				/>
			</Mui.Box>
		));

		return (
			<>
				<Mui.Typography variant="subtitle2" style={{ fontWeight: "bold", marginBottom: 8 }}>
					{label}
				</Mui.Typography>
				<div ref={inputRef} onClick={atLimit ? () => setLimitOpen(true) : undefined} style={atLimit ? { cursor: "not-allowed" } : undefined}>
					<Mui.Box display="flex" alignItems="flex-start" mb={1}>
						<Mui.TextField
							size="small"
							variant="outlined"
							label="Price Option"
							value={input}
							disabled={atLimit}
							error={!!error}
							helperText={error ? (
								<Mui.Box component="span" display="flex" alignItems="center" style={{ gap: 4 }}>
									<Icons.XCircle size={14} style={{ flexShrink: 0 }} />
									<span>{error}</span>
								</Mui.Box>
							) : " "}
							onChange={(e) => {
								const raw = e.target.value.replace(/[^\d]/g, "");
								const formatted = raw === "" ? "" : Number(raw).toLocaleString("en-US");
								setInput(formatted);
								setError("");
							}}
							onKeyPress={(e) => {
								if (e.key === "Enter" && !error && input.trim()) {
									handleAdd(input, prices, setPrices, setInput, setError);
								}
							}}
							style={{ flex: 1, marginRight: 8, pointerEvents: atLimit ? "none" : undefined }}
						/>
						<Mui.Button
							variant="text"
							color="primary"
							size="small"
							disabled={atLimit || !!error || !input.trim()}
							onClick={() => handleAdd(input, prices, setPrices, setInput, setError)}
							style={{ marginTop: 4, whiteSpace: "nowrap" }}
						>
							+ Add
						</Mui.Button>
					</Mui.Box>
				</div>
				<Mui.Popover
					open={limitOpen}
					anchorEl={inputRef.current}
					onClose={() => setLimitOpen(false)}
					anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
					transformOrigin={{ vertical: "top", horizontal: "left" }}
					PaperProps={{ className: classes.limitPopoverPaper }}
					disableRestoreFocus
				>
					<Mui.Typography variant="body2">
						Remove at least 1 price range option to add more.
					</Mui.Typography>
				</Mui.Popover>
				<Mui.Typography variant="caption" color="textSecondary" style={{ display: "block", marginBottom: 8 }}>
					{count} / {MAX_PER_SIDE} Added
				</Mui.Typography>
				<Mui.Box display="flex" flexDirection="column" alignItems="flex-start">
					{lockedPosition === "first" && lockedChip}
					{valueChips}
					{lockedPosition === "last" && lockedChip}
				</Mui.Box>
			</>
		);
	};

	const canSave = minPrices.length > 0 && maxPrices.length > 0;

	return (
		<Mui.Dialog open={open} onClose={onCancel} maxWidth="md" fullWidth>
			<Mui.DialogTitle>
				<Mui.Box display="flex" justifyContent="space-between" alignItems="center">
					<Mui.Typography variant="h6" component="h2" style={{ fontWeight: "bold", fontSize: 24 }}>Edit Price Range Options</Mui.Typography>
					<Mui.IconButton size="small" onClick={onCancel}>
						<Icons.X size={24} />
					</Mui.IconButton>
				</Mui.Box>
			</Mui.DialogTitle>
			<Mui.Divider style={{ marginLeft: 24, marginRight: 24 }} />
			<Mui.DialogContent>
				{!canSave && (
					<Mui.Box display="flex" alignItems="center" style={{ backgroundColor: "#FDECEA", borderRadius: 4, padding: "10px 14px", marginBottom: 16, gap: 8 }}>
						<Icons.AlertTriangle size={16} style={{ color: "#E57373", flexShrink: 0 }} />
						<Mui.Typography variant="body2">
							At least one Min Price and one Max Price option are required to save.
						</Mui.Typography>
					</Mui.Box>
				)}
				<Mui.Typography variant="body2" style={{ marginBottom: 16 }}>
					Add up to 10 values to each list. These appear as options in your website's price filter dropdown. No min / max are required and included by default.
				</Mui.Typography>
				<Mui.Box display="flex" alignItems="stretch">
					<Mui.Box flex={1} pr={3}>
						{renderColumn(
							"Min Price", "No Min", "first",
							minPrices, setMinPrices,
							minInput, setMinInput,
							minError, setMinError,
							minLimitOpen, setMinLimitOpen,
							minInputRef,
						)}
					</Mui.Box>
					<Mui.Divider orientation="vertical" flexItem />
					<Mui.Box flex={1} pl={3}>
						{renderColumn(
							"Max Price", "No Max", "last",
							maxPrices, setMaxPrices,
							maxInput, setMaxInput,
							maxError, setMaxError,
							maxLimitOpen, setMaxLimitOpen,
							maxInputRef,
						)}
					</Mui.Box>
				</Mui.Box>
			</Mui.DialogContent>
			<Mui.DialogActions style={{ padding: "16px 24px" }}>
				<Mui.Button variant="outlined" color="primary" onClick={onCancel}>Cancel</Mui.Button>
				<Mui.Button variant="contained" color="primary" disabled={!canSave} onClick={() => onSave(minPrices, maxPrices)}>
						Apply Changes
				</Mui.Button>
			</Mui.DialogActions>
		</Mui.Dialog>
	);
};
